import { useEffect, useMemo, useState } from 'react';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import RefreshIcon from '@mui/icons-material/Refresh';
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined';
import {
    Alert,
    Button,
    Card,
    CardContent,
    Chip,
    LinearProgress,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { Title, useNotify } from 'react-admin';

import {
    getLiveExamTaskBulkFileSyncStatus,
    queueLiveExamTaskBulkFileSync,
    stopLiveExamTaskBulkFileSync,
    type LiveExamTaskBulkFileSyncStatusRecord,
} from '../admin/examTaskReviewDataProvider';
import { formatApiDateTime } from '../admin/dateTimeUtils';

const autoRefreshIntervalMs = 10_000;
const bulkFileSyncTaskStorageKey = 'pteultra-admin:live-exam-task-bulk-file-sync-task-id';

type StatusTone = 'default' | 'success' | 'warning' | 'info' | 'error';

function readStoredTaskId(): string | null {
    if (typeof window === 'undefined') {
        return null;
    }

    const value = window.localStorage.getItem(bulkFileSyncTaskStorageKey)?.trim() ?? '';
    return value || null;
}

function storeTaskId(taskId: string | null): void {
    if (typeof window === 'undefined') {
        return;
    }

    if (!taskId) {
        window.localStorage.removeItem(bulkFileSyncTaskStorageKey);
        return;
    }

    window.localStorage.setItem(bulkFileSyncTaskStorageKey, taskId);
}

function formatStatusLabel(status: string): string {
    const trimmed = status.trim();
    if (!trimmed) {
        return 'Unknown';
    }

    return trimmed
        .split('_')
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
}

function getStatusTone(status: string): StatusTone {
    const normalized = status.trim().toLowerCase();

    if (normalized === 'succeeded') {
        return 'success';
    }

    if (normalized === 'completed_with_errors' || normalized === 'queued') {
        return 'warning';
    }

    if (normalized === 'running' || normalized === 'stop_requested') {
        return 'info';
    }

    if (normalized === 'failed') {
        return 'error';
    }

    if (normalized === 'stopped') {
        return 'warning';
    }

    return 'default';
}

function formatLocalDateTime(value: string | null | undefined): string {
    return formatApiDateTime(value, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        timeZone: 'Australia/Sydney',
    });
}

function formatPercentage(value: number): string {
    return `${Math.round(value)}%`;
}

function SummaryTile({
    label,
    value,
    helperText,
}: {
    label: string;
    value: string;
    helperText?: string;
}) {
    return (
        <Paper variant="outlined" sx={{ p: 2, minWidth: 180, flex: '1 1 180px' }}>
            <Typography variant="overline" color="text.secondary">
                {label}
            </Typography>
            <Typography variant="h5">{value}</Typography>
            {helperText ? (
                <Typography variant="body2" color="text.secondary">
                    {helperText}
                </Typography>
            ) : null}
        </Paper>
    );
}

export function LiveExamTaskBulkFileSyncPage() {
    const notify = useNotify();
    const [taskId, setTaskId] = useState<string | null>(() => readStoredTaskId());
    const [status, setStatus] = useState<LiveExamTaskBulkFileSyncStatusRecord | null>(null);
    const [errorMessage, setErrorMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isStarting, setIsStarting] = useState(false);
    const [isStopping, setIsStopping] = useState(false);
    const [lastRefreshAt, setLastRefreshAt] = useState<string | null>(null);

    useEffect(() => {
        storeTaskId(taskId);
    }, [taskId]);

    useEffect(() => {
        if (!taskId) {
            setStatus(null);
            setErrorMessage('');
            setIsLoading(false);
            return;
        }

        const currentTaskId = taskId;
        let isActive = true;

        async function loadStatus() {
            if (!isActive) {
                return;
            }

            setIsLoading(true);

            try {
                const nextStatus = await getLiveExamTaskBulkFileSyncStatus(currentTaskId);
                if (!isActive) {
                    return;
                }

                setStatus(nextStatus);
                setErrorMessage('');
                setLastRefreshAt(new Date().toISOString());
            } catch (error) {
                if (!isActive) {
                    return;
                }

                setErrorMessage(error instanceof Error ? error.message : 'Unable to load bulk sync status');
                setLastRefreshAt(new Date().toISOString());
            } finally {
                if (isActive) {
                    setIsLoading(false);
                }
            }
        }

        void loadStatus();
        const intervalId = window.setInterval(() => {
            void loadStatus();
        }, autoRefreshIntervalMs);

        return () => {
            isActive = false;
            window.clearInterval(intervalId);
        };
    }, [taskId]);

    const normalizedStatus = status?.status.trim().toLowerCase() ?? '';
    const isProcessing = ['queued', 'running', 'stop_requested'].includes(normalizedStatus);
    const canStop = Boolean(taskId) && ['queued', 'running'].includes(normalizedStatus);
    const progressValue = useMemo(() => {
        if (!status) {
            return 0;
        }

        if (status.totalTaskCount <= 0) {
            return status.isTerminal ? 100 : 0;
        }

        return Math.min(100, (status.processedTaskCount / status.totalTaskCount) * 100);
    }, [status]);

    const detailRows = useMemo(
        () => [
            ['Task ID', status?.bulkFilePublishTaskId ?? taskId ?? 'N/A'],
            ['Requested By', status?.requestedBy ?? 'N/A'],
            ['Trace ID', status?.requestTraceId ?? 'N/A'],
            ['Created At', formatLocalDateTime(status?.createdAt)],
            ['Started At', formatLocalDateTime(status?.startedAt)],
            ['Finished At', formatLocalDateTime(status?.finishedAt)],
            ['Last Refreshed', formatLocalDateTime(lastRefreshAt)],
            ['Last Exam Task ID', status?.lastExamTaskId ?? 'N/A'],
            ['Last Question No', status?.lastQuestionNo ?? 'N/A'],
        ],
        [lastRefreshAt, status, taskId],
    );

    async function handleStartSync() {
        setIsStarting(true);

        try {
            const queuedStatus = await queueLiveExamTaskBulkFileSync();
            setTaskId(queuedStatus.bulkFilePublishTaskId);
            setStatus(queuedStatus);
            setErrorMessage('');
            setLastRefreshAt(new Date().toISOString());

            notify(`Bulk sync queued. Task ID: ${queuedStatus.bulkFilePublishTaskId}`, { type: 'info' });
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unable to queue bulk sync';
            setErrorMessage(message);
            notify(message, { type: 'warning' });
        } finally {
            setIsStarting(false);
        }
    }

    async function handleRefreshNow() {
        if (!taskId) {
            return;
        }

        setIsLoading(true);

        try {
            const nextStatus = await getLiveExamTaskBulkFileSyncStatus(taskId);
            setStatus(nextStatus);
            setErrorMessage('');
            setLastRefreshAt(new Date().toISOString());
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unable to refresh bulk sync status';
            setErrorMessage(message);
            setLastRefreshAt(new Date().toISOString());
            notify(message, { type: 'warning' });
        } finally {
            setIsLoading(false);
        }
    }

    async function handleStopSync() {
        if (!taskId) {
            return;
        }

        setIsStopping(true);

        try {
            const nextStatus = await stopLiveExamTaskBulkFileSync(taskId);
            const nextNormalizedStatus = nextStatus.status.trim().toLowerCase();

            setStatus(nextStatus);
            setErrorMessage('');
            setLastRefreshAt(new Date().toISOString());

            notify(nextNormalizedStatus === 'stopped' ? 'Bulk sync stopped.' : 'Bulk sync stop requested.', {
                type: 'info',
            });
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unable to stop bulk sync';
            setErrorMessage(message);
            notify(message, { type: 'warning' });
        } finally {
            setIsStopping(false);
        }
    }

    return (
        <Card>
            <Title title="Sync Files" />
            {isLoading || isStarting || isStopping ? <LinearProgress /> : null}
            <CardContent>
                <Stack spacing={3}>
                    <Stack spacing={1}>
                        <Typography variant="h4">Sync Files</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Queue a bulk live exam task file sync and monitor progress across all destination exam tasks. This page refreshes status every 10 seconds.
                        </Typography>
                    </Stack>

                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between">
                            <Stack spacing={1}>
                                <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                                    <Chip
                                        label={status ? formatStatusLabel(status.status) : 'No Task Selected'}
                                        color={getStatusTone(status?.status ?? '')}
                                        variant="outlined"
                                        size="small"
                                    />
                                    <Chip label="Auto refresh: 10s" variant="outlined" size="small" />
                                </Stack>
                                <Typography variant="body2" color="text.secondary">
                                    {taskId
                                        ? `Tracking task ${taskId}`
                                        : 'Start a bulk sync to begin tracking progress on this page.'}
                                </Typography>
                            </Stack>

                            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                <Button
                                    variant="outlined"
                                    startIcon={<RefreshIcon />}
                                    onClick={() => {
                                        void handleRefreshNow();
                                    }}
                                    disabled={!taskId || isLoading || isStarting || isStopping}
                                >
                                    Refresh Now
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="warning"
                                    startIcon={<StopCircleOutlinedIcon />}
                                    onClick={() => {
                                        void handleStopSync();
                                    }}
                                    disabled={!canStop || isLoading || isStarting || isStopping}
                                >
                                    {normalizedStatus === 'stop_requested' ? 'Stop Requested' : 'Stop Sync'}
                                </Button>
                                <Button
                                    variant="contained"
                                    startIcon={<PlayArrowRoundedIcon />}
                                    onClick={() => {
                                        void handleStartSync();
                                    }}
                                    disabled={isStarting || isStopping || isProcessing}
                                >
                                    {normalizedStatus === 'stop_requested'
                                        ? 'Stopping Sync'
                                        : isProcessing
                                          ? 'Sync Running'
                                          : 'Start Sync Files'}
                                </Button>
                            </Stack>
                        </Stack>
                    </Paper>

                    {errorMessage ? <Alert severity="warning">{errorMessage}</Alert> : null}

                    {!status ? (
                        <Alert severity="info">
                            No bulk sync run is currently loaded. Start a new run from this page to see live progress.
                        </Alert>
                    ) : (
                        <>
                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Stack spacing={1.5}>
                                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }} justifyContent="space-between">
                                        <Typography variant="h6">Progress</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {status.processedTaskCount} of {status.totalTaskCount} exam tasks processed
                                        </Typography>
                                    </Stack>
                                    <LinearProgress variant="determinate" value={progressValue} sx={{ height: 10, borderRadius: 999 }} />
                                    <Typography variant="body2" color="text.secondary">
                                        {formatPercentage(progressValue)} complete
                                    </Typography>
                                </Stack>
                            </Paper>

                            <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                                <SummaryTile label="Total Tasks" value={String(status.totalTaskCount)} />
                                <SummaryTile label="Processed" value={String(status.processedTaskCount)} helperText={formatPercentage(progressValue)} />
                                <SummaryTile label="Succeeded" value={String(status.succeededTaskCount)} />
                                <SummaryTile label="Failed" value={String(status.failedTaskCount)} />
                            </Stack>

                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Stack spacing={1.5}>
                                    <Typography variant="h6">Run Details</Typography>
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 700 }}>Field</TableCell>
                                                    <TableCell sx={{ fontWeight: 700 }}>Value</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {detailRows.map(([label, value]) => (
                                                    <TableRow key={label}>
                                                        <TableCell sx={{ whiteSpace: 'nowrap', width: '28%' }}>{label}</TableCell>
                                                        <TableCell>{value}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Stack>
                            </Paper>

                            {status.lastError ? (
                                <Alert severity={normalizedStatus === 'failed' || status.failedTaskCount > 0 ? 'error' : 'warning'}>
                                    {status.lastError}
                                </Alert>
                            ) : null}
                        </>
                    )}
                </Stack>
            </CardContent>
        </Card>
    );
}